import { makeStyles } from "@mui/styles";

export interface DonutProps {
  /**
   * Percentage value from 0-100.
   * Defaults to value prop if undefined
   */
  centerValue?: number;
  /**
   * Percentage value from 0-100.
   */
  value: number;
  /**
   * Adding this number to `value` should be <=100.
   */
  secondaryValue?: number;
  /**
   * Renders in the circle, under the percentage number.
   */
  label: string;
  /**
   * Renders a smaller variation
   */
  small?: boolean;
  /**
   * Renders a medium variation
   */
  medium?: boolean;
  /**
   * Should describe the values that are displayed. e.g. "Pie Chart: Student has answered 40% of questions correct"
   */
  "aria-label": string;

  extraSmall?: boolean;

  tiny?: boolean;
}

const useStyles = makeStyles({
  root: {
    position: "relative",
    minWidth: (props: DonutProps) => {
      if (props.tiny) {
        return 32;
      }
      if (props.extraSmall) {
        return 40;
      }
      if (props.medium) {
        return 48;
      }
      if (props.small) {
        return 90;
      }
      return 192;
      //props.small ? 90 : 192
    },
    aspectRatio: "1",
    display: "inline-grid",
    placeItems: "center",
  },
  donut: {
    width: "100%",
    aspectRatio: "1",
    borderRadius: "50%",
    position: "absolute",
  },
  center: (props) => {
    let width = "80%";
    if (props.tiny) {
      width = "24px";
    }
    if (props.extraSmall) {
      width = "60%";
    }
    if (props.medium) {
      width = "67%";
    }
    if (props.small) {
      width = "78%";
    }
    return {
      width: width,
      aspectRatio: "1",
      position: "absolute",
      display: "inline-grid",
      placeItems: "center",
      placeContent: "center",
      backgroundColor: "white",
      borderRadius: "50%",
      zIndex: 1,
    };
  },
  label: (props) => ({
    width: "73%",
    aspectRatio: "1",
    borderRadius: "50%",
    textAlign: "center",
    display: "grid",
    placeItems: "center",
    placeContent: "center",
    color: "#484848",
    lineHeight: "1",
    "& > :first-child": {
      fontSize: props.small || props.medium ? 18 : 30,
      fontWeight: "700",
      marginBottom: 4,
    },
    "& > :nth-child(2)": {
      fontSize: 14,
      fontWeight: "400",
    },
  }),
});

export const Donut = (props: DonutProps) => {
  const classes = useStyles(props);
  const { extraSmall, tiny, medium } = props;
  const value = Number.isNaN(props.value) ? 0 : props.value;
  const secondaryValue = Number.isNaN(props.secondaryValue) ? 0 : props.secondaryValue;
  const centerValue = props.centerValue
    ? Number.isNaN(props.centerValue)
      ? 0
      : props.centerValue
    : value;

  const getCircleBackground = () => {
    const primaryColor =
      value >= 50
        ? // green
          "#3AA800"
        : // Orange
        value >= 25
        ? "#FFC627"
        : "#F76205";
    if (secondaryValue) {
      return `conic-gradient(${primaryColor} ${value}%, #00000080 ${value}% ${secondaryValue}%, lightgray ${secondaryValue}%)`;
    } else {
      return `conic-gradient(${primaryColor} ${value}%, #F2F2F2 ${value}% )`;
    }
  };

  return (
    <div className={classes.root} aria-label={props["aria-label"]}>
      {/* aria-hidden because this is described by the above aria-label */}
      <div className={classes.center} aria-hidden>
        {!extraSmall && !tiny && !medium && (
          <div className={classes.label} aria-hidden>
            <div aria-hidden>{centerValue}%</div>
            <div aria-hidden>{props.label}</div>
          </div>
        )}
      </div>
      <div
        className={classes.donut}
        style={{
          background: getCircleBackground(),
        }}
      />
    </div>
  );
};
